import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus, RotateCcw, Volume2 } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import TasbihIcon from '../components/TasbihIcon';

const TasbihPage = () => {
  const { settings } = useSettings();
  const [count, setCount] = useState(0);
  const [target, setTarget] = useState(33);
  const [vibrationEnabled, setVibrationEnabled] = useState(true);

  const targets = [33, 99, 100, 1000];

  const handleIncrement = () => {
    const newCount = count + 1;
    setCount(newCount);

    if (vibrationEnabled && navigator.vibrate) {
      navigator.vibrate(10);
    }

    if (newCount === target) {
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
      }
      playSound();
    }
  };

  const handleReset = () => {
    setCount(0);
    if (vibrationEnabled && navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  const playSound = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  };

  const progress = (count / target) * 100;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gradient-to-b from-emerald-50 to-emerald-100 px-6 py-8 pb-24 flex flex-col"
    >
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold tracking-tight text-primary-dark mb-2">
          Tasbih Digital
        </h1>
        <p className="text-sm font-body text-text-secondary">
          Hitung dzikir dengan mudah
        </p>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center">
        {/* Counter Display */}
        <div className="relative mb-12">
          <svg className="transform -rotate-90 w-64 h-64">
            <circle
              cx="128"
              cy="128"
              r="100"
              stroke="#E7E5E4"
              strokeWidth="12"
              fill="none"
            />
            <motion.circle
              cx="128"
              cy="128"
              r="100"
              stroke="#065F46"
              strokeWidth="12"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 100}`}
              initial={{ strokeDashoffset: 2 * Math.PI * 100 }}
              animate={{ strokeDashoffset: 2 * Math.PI * 100 * (1 - progress / 100) }}
              transition={{ duration: 0.3 }}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-7xl font-body font-bold text-emerald-800 tabular-nums">
                {count}
              </div>
              <div className="text-sm font-body text-text-secondary mt-2">
                Target: {target}
              </div>
            </div>
          </div>
        </div>

        {/* Main Tasbih Button */}
        <motion.button
          data-testid="tasbih-counter-btn"
          onClick={handleIncrement}
          whileTap={{ scale: 0.95 }}
          className="w-48 h-48 rounded-full bg-white shadow-[inset_0_-8px_12px_rgba(0,0,0,0.05),0_8px_20px_rgba(0,0,0,0.08)] border-4 border-stone-100 active:shadow-inner flex flex-col items-center justify-center gap-2 mb-8"
        >
          <TasbihIcon variant={settings.tasbih_icon || 'classic'} className="w-10 h-10 text-emerald-700" />
          <span className="text-2xl font-heading font-bold text-emerald-800">Tekan</span>
        </motion.button>

        {/* Target Selector */}
        <div className="flex gap-2 mb-6">
          {targets.map((t) => (
            <button
              key={t}
              data-testid={`target-btn-${t}`}
              onClick={() => {
                setTarget(t);
                setCount(0);
              }}
              className={`px-4 py-2 rounded-full font-body font-medium text-sm transition-colors ${
                target === t
                  ? 'bg-emerald-700 text-white'
                  : 'bg-white text-emerald-700 border border-emerald-200'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Controls */}
        <div className="flex gap-4">
          <button
            data-testid="tasbih-reset-btn"
            onClick={handleReset}
            className="px-6 py-3 rounded-full bg-white border border-stone-200 text-text-primary font-body font-medium flex items-center gap-2 hover:bg-stone-50 transition-colors"
          >
            <RotateCcw className="w-5 h-5" />
            Reset
          </button>
          <button
            data-testid="tasbih-vibration-toggle"
            onClick={() => setVibrationEnabled(!vibrationEnabled)}
            className={`px-6 py-3 rounded-full font-body font-medium flex items-center gap-2 transition-colors ${
              vibrationEnabled
                ? 'bg-emerald-700 text-white'
                : 'bg-white border border-stone-200 text-text-primary'
            }`}
          >
            <Volume2 className="w-5 h-5" />
            {vibrationEnabled ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default TasbihPage;